import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import * as Context from 'Context';
import {ActionMethod, ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  AutomationInputFileConfigSpec,
  ClientAutomationOutputFileConfigSpec,
} from 'lab-automation/settings/RunSchemaSpecConstants';
import {duplicateRunSchema} from 'runs/run-schemas/RunSchemaActions';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import IndeterminateCheckbox from 'ui-library/IndeterminateCheckbox';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import TextField from 'ui-library/inputs/TextField';
import Modal from 'ui-library/modals/Modal';
import runModal from 'ui-library/runModal';
import {withLoadingScreen} from 'util/Promises';

interface CheckboxGroupProps {
  title: string;
  values: Array<string>;
  excludedValues: Set<string>;
  setExcludedValues: (values: Set<string>) => void;
}

function CheckboxGroup(props: CheckboxGroupProps): JSX.Element | null {
  if (!props.values.length) {
    return null;
  }
  return (
    <div className="u-spacing-top">
      <IndeterminateCheckbox
        items={props.values}
        selectedMap={(value) => !props.excludedValues.has(value)}
        onChange={(e) => {
          if (e.target.checked) {
            props.setExcludedValues(new Set());
          } else {
            props.setExcludedValues(new Set(props.values));
          }
        }}
      />
      <span className="font-proxima-semibold fieldLabel-label u-mini-spacing-left">{props.title}</span>
      {props.values.map((value) => (
        <CheckboxInput
          id={_.uniqueId()}
          key={value}
          label={value}
          checked={!props.excludedValues.has(value)}
          className="u-spacing-left"
          onChange={(e) => {
            if (e.target.checked) {
              const newExcludedValues = new Set(props.excludedValues);
              newExcludedValues.delete(value);
              props.setExcludedValues(newExcludedValues);
            } else {
              const newExcludedValues = new Set(props.excludedValues);
              newExcludedValues.add(value);
              props.setExcludedValues(newExcludedValues);
            }
          }}
        />
      ))}
    </div>
  );
}

interface DuplicateRunSchemaModalProps {
  schemaId: string;
  sessionId: string;
  isSpecJsonFormat: boolean;
  inputFileConfigs: Array<AutomationInputFileConfigSpec> | null;
  outputFileConfigs: Array<ClientAutomationOutputFileConfigSpec> | null;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

export function DuplicateRunSchemaModal(props: DuplicateRunSchemaModalProps): JSX.Element {
  const [name, setName] = useState('');
  const [sqlIdentifier, setSqlIdentifier] = useState('');
  const [excludedInputFileConfigs, setExcludedInputFileConfigs] = useState<Set<string>>(new Set());
  const [excludedOutputFileConfigs, setExcludedOutputFileConfigs] = useState<Set<string>>(new Set());
  const inputFileConfigNames = props.inputFileConfigs
    ? props.inputFileConfigs.map((config) => config.name)
    : [];
  const outputFileConfigNames = props.outputFileConfigs
    ? props.outputFileConfigs.map((config) => config.name)
    : [];
  return (
    <Modal
      title="Duplicate run schema"
      onRequestClose={props.onRequestClose}
      onRequestSubmit={() => {
        Context.using(
          {
            analyticsMetadata: {
              actionOrigin: ActionOrigin.SETTINGS_ASSAY_SCHEMA_PAGE_UI,
              actionMethod: ActionMethod.DUPLICATE_RUN_SCHEMA_BUTTON,
            },
          },
          null,
          async () => {
            await withLoadingScreen(
              duplicateRunSchema({
                schemaId: props.schemaId,
                name,
                sqlIdentifier: sqlIdentifier || null,
                sessionId: props.sessionId,
                isSpecJsonFormat: props.isSpecJsonFormat,
                excludedInputFileConfigs: [...excludedInputFileConfigs],
                excludedOutputFileConfigs: [...excludedOutputFileConfigs],
              }),
              'Duplicating run schema...'
            );
            props.onRequestSubmit();
          }
        );
      }}
      buttons={[
        {
          style: ButtonStyle.SECONDARY,
          submit: true,
          text: 'Submit',
        },
      ]}
    >
      <TextField
        className={css(styles.textInput)}
        label="Name"
        isRequired={true}
        value={name}
        onChange={(e) => {
          setName(e.target.value);
        }}
      />
      {BenchlingConfig.ENABLE_WAREHOUSE && BenchlingConfig.SCHEMAS_ENABLE_CHANGE_WAREHOUSE_NAME ? (
        <TextField
          className={`${css(styles.textInput)} u-small-spacing-top`}
          label="Warehouse name"
          value={sqlIdentifier}
          onChange={(e) => {
            setSqlIdentifier(e.target.value);
          }}
        />
      ) : null}
      <CheckboxGroup
        title="Input file configuration"
        values={inputFileConfigNames}
        excludedValues={excludedInputFileConfigs}
        setExcludedValues={setExcludedInputFileConfigs}
      />
      <CheckboxGroup
        title="Output file configuration"
        values={outputFileConfigNames}
        excludedValues={excludedOutputFileConfigs}
        setExcludedValues={setExcludedOutputFileConfigs}
      />
    </Modal>
  );
}

interface DuplicateRunSchemaButtonProps {
  className?: string;
  schemaId: string;
  sessionId: string;
  hasUnsavedData: boolean;
  isSpecJsonFormat: boolean;
  inputFileConfigs: Array<AutomationInputFileConfigSpec> | null;
  outputFileConfigs: Array<ClientAutomationOutputFileConfigSpec> | null;
}

export default function DuplicateRunSchemaButton(props: DuplicateRunSchemaButtonProps): JSX.Element | null {
  return (
    <Tooltip title="Schema must be saved before duplicating" disabled={!props.hasUnsavedData}>
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        className={props.className}
        disabled={props.hasUnsavedData}
        onClick={() =>
          runModal(DuplicateRunSchemaModal, {
            schemaId: props.schemaId,
            sessionId: props.sessionId,
            isSpecJsonFormat: props.isSpecJsonFormat,
            inputFileConfigs: props.inputFileConfigs,
            outputFileConfigs: props.outputFileConfigs,
          })
        }
        size={ButtonSize.SMALL}
        text="Duplicate schema"
      />
    </Tooltip>
  );
}

const styles = StyleSheet.create({
  textInput: {
    width: '50%',
  },
});
